import { useEffect, useState, type ReactNode } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';
import { radius } from '@karehero/llama/theme/radius';

import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import Canvas from '@/designs-dev/handover/Canvas';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import { Segment } from '@/designs-dev/inspect/Pill';
import { useForcedStates } from '@/designs-dev/library/forceStates';
import {
  type LibraryEntry,
  type LibraryPage,
  type Matrix,
} from '@/designs-dev/library/library';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';

const COLUMNS = 3;

/**
 * The outline a Figma component set draws around its variants. Marine Green
 * rather than Figma's purple, which the palette does not have.
 */
export const setSx = {
  p: 3,
  border: '1px dashed',
  borderColor: 'primary.main',
  borderRadius: 1,
} as const;

export const Caption = ({ children }: { children: string }) => (
  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
    {children}
  </Typography>
);

/**
 * Two properties at once, the way a component set is arranged in Figma: one
 * down the side, one across the top, so a row reads one variant through every
 * state and a column reads one state across every variant.
 */
const MatrixSet = ({ matrix }: { matrix: Matrix }) => (
  <Stack spacing={1}>
    {matrix.title && <Caption>{matrix.title}</Caption>}
    <Box
      sx={{
        ...setSx,
        display: 'grid',
        gridTemplateColumns: `max-content repeat(${matrix.columns.length}, max-content)`,
        columnGap: 4,
        rowGap: 3,
        alignItems: 'center',
        width: 'max-content',
      }}
    >
      <Box />
      {matrix.columns.map((c) => (
        <Caption key={c}>{c}</Caption>
      ))}
      {matrix.rows.map((row) => [
        <Caption key={row.label}>{row.label}</Caption>,
        ...row.cells.map((cell, i) => (
          <Box key={`${row.label}-${matrix.columns[i]}`}>{cell}</Box>
        )),
      ])}
    </Box>
  </Stack>
);

/** One property: every variant side by side, each with its caption under it. */
const RowSet = ({
  states,
}: {
  states: NonNullable<LibraryEntry['states']>;
}) => (
  <Stack
    direction='row'
    spacing={4}
    useFlexGap
    sx={{ ...setSx, flexWrap: 'wrap', alignItems: 'flex-start' }}
  >
    {states.map((s) => (
      <Stack key={s.label} spacing={1} sx={{ alignItems: 'flex-start' }}>
        {s.node}
        <Caption>{s.label}</Caption>
      </Stack>
    ))}
  </Stack>
);

export const ComponentSet = ({
  entry,
  old = false,
}: {
  entry: LibraryEntry;
  /** The component another replaced, marked so nobody reaches for it. */
  old?: boolean;
}) => (
  <Stack spacing={1.5}>
    <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
      <Typography variant='h6'>{entry.name}</Typography>
      {old && <Chip size='small' label='Old' />}
    </Stack>
    {entry.matrices?.map((m, i) => (
      <MatrixSet key={i} matrix={m} />
    ))}
    {entry.states && entry.states.length > 0 && (
      <RowSet states={entry.states} />
    )}
    {entry.old && <ComponentSet entry={entry.old} old />}
  </Stack>
);

/** A section of a library page: its name above, a Floral White frame below. */
export const Frame = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Stack spacing={1.5}>
    <Typography variant='overline' sx={{ color: 'text.secondary' }}>
      {title}
    </Typography>
    {/* Card's look, drawn on a Box: drill mode selects the outermost
        component under a click, and a Card here would be every one of them. */}
    <Box
      sx={{
        p: 4,
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: `${radius.md}px`,
        bgcolor: 'custom.neutral.50',
      }}
    >
      <Stack spacing={5}>{children}</Stack>
    </Box>
  </Stack>
);

/**
 * Frames three to a row, each column as wide as its widest frame, so the page
 * reads across like a library page rather than down like a document.
 */
export const FrameGrid = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${COLUMNS}, max-content)`,
      gap: 6,
      alignItems: 'start',
    }}
  >
    {children}
  </Box>
);

/** A library page's canvas: its title, then each section's frames. */
type ShellProps = { title: string; children: ReactNode };

/**
 * The page on its canvas, with drill mode: the whole page is the "screen" the
 * inspect overlay reads, so any component on it can be clicked open in the
 * layers and inspect panels, the way a prototype's can.
 */
const Shell = ({ title, children }: ShellProps) => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const [screenEl, setScreenEl] = useState<HTMLElement | null>(null);

  // Esc is staged, as on the prototypes: a first press clears the selection,
  // a second leaves drill mode.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setCurrent(null);
      setHovered(null);
      if (!current) setActive(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, setActive, setCurrent, setHovered]);
  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Canvas
          tools={
            <Segment
              icon='code'
              label='Inspect'
              selected={active}
              onClick={() => {
                setActive(!active);
                setCurrent(null);
                setHovered(null);
              }}
            />
          }
        >
          <Box
            ref={setScreenEl}
            data-drill-root='true'
            sx={{ position: 'relative' }}
          >
            <PhoneScreenContext.Provider value={screenEl}>
              <Stack spacing={8} sx={{ px: 6, py: 6 }}>
                <Typography variant='h1'>{title}</Typography>
                {children}
              </Stack>
              <InspectOverlay editable={false} />
            </PhoneScreenContext.Provider>
          </Box>
        </Canvas>
      </Box>
      {/* Docked beside the canvas, as on the prototypes. Both render nothing
          until inspect is on. */}
      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

export const LibraryShell = (props: ShellProps) => (
  // The newest onboarding's style sheet, so chat replies draw in Gardenia
  // Dark as designed rather than the coral the older intake still uses.
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <InspectProvider>
      <Shell {...props} />
    </InspectProvider>
  </IntakeStyleContext.Provider>
);

/**
 * A page of the library: every component on it drawn live from the real code,
 * laid out like a library page in Figma, a frame per group and a component set
 * per component.
 */
const LibraryCanvas = ({ page }: { page: LibraryPage }) => {
  useForcedStates();
  return (
    <LibraryShell title={page.title}>
      {page.sections.map((section, i) => (
        <Stack key={section.title ?? i} spacing={3}>
          {section.title && (
            <Typography variant='h2'>{section.title}</Typography>
          )}
          <FrameGrid>
            {section.groups.map((group) => (
              <Frame
                key={`${group.title}-${group.components[0]?.name}`}
                title={group.title}
              >
                {group.components.map((entry) => (
                  <ComponentSet key={entry.name} entry={entry} />
                ))}
              </Frame>
            ))}
          </FrameGrid>
        </Stack>
      ))}
    </LibraryShell>
  );
};

export default LibraryCanvas;
